import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { loginUser } from "../../store/UserSlice";
import { AppDispatch } from "../../store/store";
import { loginSchema, LoginFormData } from "../../schemas/auth.schema";
import Input from "../form/Input";
import Button from "../form/Button";

import apiClient from "../../api/apiClient";

interface LoginFormProps {
  onForgotPassword: () => void;
}

export default function LoginForm({ onForgotPassword }: LoginFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    mode: "onSubmit",
  });

  const handleResendFromLogin = async () => {
    if (!unverifiedEmail) return;
    try {
      setResending(true);
      const res = await apiClient.post("/api/v1/auth/resend-verification", {
        email: unverifiedEmail,
      });
      setResendSent(true);
      toast.success(
        res.data?.message ||
          t("verification.resendSuccess", "Fresh verification link sent! Please check your email.")
      );
    } catch (err: any) {
      toast.error(
        err.response?.data?.error?.message ||
          t("verification.resendError", "Failed to send verification link")
      );
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setUnverifiedEmail(null);
    setResendSent(false);

    const result = await dispatch(loginUser(data));

    if (loginUser.fulfilled.match(result) && result.payload) {
      toast.success(t("login.loginSuccess", "Logged In successfully"));

      const slugData = localStorage.getItem("SetSlug");
      if (slugData) {
        navigate(`/program/${slugData}`);
        return;
      }

      const loginData = localStorage.getItem("loginData");
      if (loginData) {
        try {
          const parsedData = JSON.parse(loginData);
          if (parsedData?.user?.isAdmin === true) {
            navigate("/admin/dashboard");
          } else {
            navigate("/bookings");
          }
        } catch (error) {
          console.error("Unable to parse login data:", error);
          navigate("/bookings");
        }
        return;
      }

      navigate("/bookings");
    } else if (loginUser.rejected.match(result)) {
      const errorMsg = String(result.payload || "");
      if (
        errorMsg.toLowerCase().includes("grace period") ||
        errorMsg.toLowerCase().includes("verify your email")
      ) {
        setUnverifiedEmail(data.email);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-5">
      {/* Unverified Email Grace-Period Expired Banner */}
      {unverifiedEmail && (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl text-left">
          <div className="flex items-start gap-3">
            <span className="text-xl shrink-0 mt-0.5">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-amber-950">
                {t("verification.graceExpiredTitle", "Verification Grace Period Expired")}
              </h4>
              <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
                {t(
                  "verification.graceExpiredLoginDesc",
                  "Your 48-hour email verification grace period has expired. Please verify your email to continue logging in."
                )}
              </p>

              <div className="mt-3">
                {resendSent ? (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg inline-block border border-emerald-200">
                    ✓ {t("verification.resendSuccess", "Verification link sent! Check your email.")}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendFromLogin}
                    disabled={resending}
                    className="bg-[#037897] hover:bg-[#025c74] text-white text-xs font-bold px-3.5 py-1.5 rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
                  >
                    {resending ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t("verification.sending", "Sending...")}</span>
                      </>
                    ) : (
                      <>
                        <span>✉️</span>
                        <span>{t("verification.resendLinkAction", "Resend Verification Link")}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="text-center">
        <Link to="/" className="inline-block">
          <img
            src="./images/bluelogo-new.png"
            alt="Logo"
            className="mx-auto mb-3 max-w-[130px] md:max-w-[150px]"
          />
        </Link>

        <h2 className="mb-2 font-gilroy text-[#263238] font-semibold text-[26px] md:text-[30px]">
          {t("login.title", "Log in to your account")}
        </h2>

        <p className="text-gray-7 text-[#78909C] font-gilroy text-[16px] md:text-[18px]">
          {t(
            "login.subtitle",
            "Welcome back! Sign in to access your account and manage your healthcare needs."
          )}
        </p>
      </div>

      {/* Email */}
      <Input
        id="email"
        label={t("login.emailLabel", "Email Address")}
        type="email"
        placeholder={t("login.emailPlaceholder", "Enter your email")}
        autoComplete="email"
        error={errors.email?.message}
        {...register("email")}
      />

      {/* Password */}
      <Input
        id="password"
        label={t("login.passwordLabel", "Password")}
        type="password"
        placeholder={t("login.passwordPlaceholder", "Enter your password")}
        autoComplete="current-password"
        showPasswordToggle
        error={errors.password?.message}
        {...register("password")}
      />

      {/* Remember Me & Forgot Password */}
      <div className="flex flex-row items-center justify-between font-gilroy-medium text-[#263238]">
        <div className="flex">
          <input
            type="checkbox"
            id="rememberMe"
            {...register("rememberMe")}
            className="peer"
          />

          <label
            htmlFor="rememberMe"
            className="flex items-center cursor-pointer"
          >
            <span className="ml-2 text-sm text-[#263238]">
              {t("login.rememberMe", "Remember me")}
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-[#037897] hover:underline"
        >
          {t("login.forgotPassword", "Forgot password?")}
        </button>
      </div>

      {/* Login Button */}
      <Button
        isLoading={isSubmitting}
        loadingText={t("login.loggingIn", "Logging in...")}
        className="!mt-2"
      >
        {t("login.loginBtn", "Login")}
      </Button>

      {/* Register Link */}
      <h4 className="text-center text-sm sm:text-base">
        {t("login.noAccount", "Don't have an account?")}{" "}
        <Link to="/register" className="text-[#037897] font-semibold">
          {t("login.registerLink", "Register")}
        </Link>
      </h4>
    </form>
  );
}
